import type { Components, Theme } from "@mui/material";

declare module "@mui/material/FormControlLabel" {
	interface FormControlLabelProps {
		variant?: "fancy";
		color?: "audiences" | "creators";
	}

	interface FormControlLabelPropsVariantOverrides {
		fancy: true;
	}

	interface FormControlLabelPropsColorOverrides {
		audiences: true;
		creators: true;
	}
}

export const MuiFormControlLabel: Components<Theme>["MuiFormControlLabel"] = {
	variants: [
		{
			props: { variant: "fancy" },
			style: ({ theme }) => ({
				margin: 0,
				".MuiFormControlLabel-label": {
					backgroundColor: theme.palette.background.default,
					borderColor: theme.palette.border,
					borderRadius: theme.spacing(2),
					borderStyle: "solid",
					borderWidth: 1,
					color: theme.palette.text.primary,
					display: "flex",
					gap: theme.spacing(1.5),
					alignItems: "center",
					height: theme.spacing(5.5),
					paddingInline: theme.spacing(1.5),
					width: "100%",
					svg: {
						color: theme.palette.text.disabled,
						height: theme.spacing(2.75),
						width: theme.spacing(2.75),
					},
				},
				"& .Mui-disabled + .MuiFormControlLabel-label": {
					backgroundColor: theme.palette.muted.main,
					color: theme.palette.text.disabled,
				},
				".MuiFormControlLabel-label:hover, & .Mui-checked + .MuiFormControlLabel-label":
					{
						backgroundColor: theme.palette.muted.main,
						borderColor: theme.palette.primary.light,
						color: theme.palette.primary.main,
						svg: { color: theme.palette.primary.light },
					},
				"& .Mui-focusVisible + .MuiFormControlLabel-label": {
					outlineColor: theme.palette.primary.main,
					outlineOffset: -1,
					outlineStyle: "solid",
					outlineWidth: 2,
				},
			}),
		},
		createFormControlLabelFancyColor("audiences"),
		createFormControlLabelFancyColor("creators"),
	],
};

function createFormControlLabelFancyColor(color: "audiences" | "creators") {
	return {
		props: { variant: "fancy" as const, color },
		style: ({ theme }: { theme: Theme }) => {
			const palette = theme.palette[color];
			return {
				".MuiFormControlLabel-label:hover, & .Mui-checked + .MuiFormControlLabel-label":
					{
						backgroundColor: palette.background,
						borderColor: palette.light,
						color: palette.main,
						svg: { color: palette.light },
					},
				"& .Mui-focusVisible + .MuiFormControlLabel-label": {
					outlineColor: palette.light,
				},
			};
		},
	};
}
